import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import Header from '../components/shared/Header'
import Footer from '../components/shared/Footer'

export default function AdminBlogPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [authenticated, setAuthenticated] = useState(false)
  const [token, setToken] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    methodology: '',
    findings: '',
    implications: '',
    research_area: '',
    tags: '',
    published: false,
    reading_time: '',
    validation_status: 'preliminary'
  })

  // Check authentication
  useEffect(() => {
    const savedToken = sessionStorage.getItem('stats_token')
    if (savedToken) {
      setToken(savedToken)
      setAuthenticated(true)
      if (slug) {
        loadPost(savedToken)
      }
    }
  }, [slug])

  // Load existing post for editing
  const loadPost = async (authToken) => {
    try {
      const response = await axios.get(`/api/v1/research/${slug}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      })
      const post = response.data
      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        methodology: post.methodology || '',
        findings: post.findings || '',
        implications: post.implications || '',
        research_area: post.research_area || '',
        tags: (post.tags || []).join(', '),
        published: post.published || false,
        reading_time: post.reading_time?.toString() || '',
        validation_status: post.validation_status || 'preliminary'
      })
    } catch (err) {
      setError('Failed to load post: ' + (err.message || 'Unknown error'))
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const loginResponse = await axios.post('/api/v1/auth/login', {
        username: 'admin',
        password: password
      })
      
      const authToken = loginResponse.data.access_token
      setToken(authToken)
      setAuthenticated(true)
      sessionStorage.setItem('stats_token', authToken)
      setPassword('')
      
      if (slug) {
        await loadPost(authToken)
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid password')
      } else {
        setError('Error logging in: ' + (err.message || 'Unknown error'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      const tagsArray = formData.tags.split(',').map(t => t.trim()).filter(t => t)
      const payload = {
        ...formData,
        tags: tagsArray,
        reading_time: formData.reading_time ? parseInt(formData.reading_time) : null
      }

      if (slug) {
        // Update existing post
        await axios.put(`/api/v1/research/${slug}`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        navigate(`/notes/${formData.slug}`)
      } else {
        // Create new post
        const response = await axios.post('/api/v1/research', payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        navigate(`/notes/${response.data.slug}`)
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.')
        setAuthenticated(false)
        sessionStorage.removeItem('stats_token')
      } else {
        setError('Error saving: ' + (err.response?.data?.detail || err.message || 'Unknown error'))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  // Auto-generate slug from title
  useEffect(() => {
    if (!slug && formData.title && !formData.slug) {
      const generatedSlug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
      setFormData(prev => ({ ...prev, slug: generatedSlug }))
    }
  }, [formData.title, slug])

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-bg-dark text-text-dark">
        <div className="max-w-container mx-auto px-5 py-8">
          <Header />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-full max-w-md">
              <div className="bg-surface-dark-1 rounded-lg p-8 border border-border-dark">
                <h1 className="text-2xl font-semibold mb-6">Admin Login</h1>
                <form onSubmit={handleLogin}>
                  <div className="mb-4">
                    <label htmlFor="password" className="block text-sm font-medium mb-2">
                      Admin Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
                      placeholder="Enter admin password"
                      autoFocus
                      required
                    />
                  </div>
                  {error && (
                    <div className="mb-4 text-red-400 text-sm">{error}</div>
                  )}
                  <button
                    type="submit"
                    disabled={loading || !password}
                    className="w-full px-4 py-2 bg-accent-dark text-white rounded-lg hover:bg-accent-dark/90 disabled:opacity-50"
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </button>
                </form>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg-dark text-text-dark">
      <div className="max-w-4xl mx-auto px-5 py-8">
        <Header />
        
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {slug ? 'Edit Research Note' : 'Create Research Note'}
            </h1>
            <p className="text-muted-dark">Create or edit experimental observations</p>
          </div>
          <button
            onClick={() => navigate('/notes')}
            className="px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg hover:bg-surface-dark-3 text-sm"
          >
            Back to Notes
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-400/10 border border-red-400/30 rounded-lg text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
              required
            />
          </div>

          {/* Slug */}
          <div>
            <label htmlFor="slug" className="block text-sm font-medium mb-2">
              Slug (URL) *
            </label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark font-mono text-sm"
              required
            />
          </div>

          {/* Excerpt */}
          <div>
            <label htmlFor="excerpt" className="block text-sm font-medium mb-2">
              Excerpt/Summary *
            </label>
            <textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
              required
            />
          </div>

          {/* Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium mb-2">
              Content (HTML) *
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows={20}
              className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark font-mono text-sm"
              required
            />
          </div>

          {/* Research Area & Methodology */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="research_area" className="block text-sm font-medium mb-2">
                Research Area
              </label>
              <input
                type="text"
                id="research_area"
                name="research_area"
                value={formData.research_area}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
              />
            </div>
            <div>
              <label htmlFor="methodology" className="block text-sm font-medium mb-2">
                Methodology
              </label>
              <input
                type="text"
                id="methodology"
                name="methodology"
                value={formData.methodology}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
              />
            </div>
          </div>

          {/* Findings & Implications */}
          <div>
            <label htmlFor="findings" className="block text-sm font-medium mb-2">
              Key Findings (HTML)
            </label>
            <textarea
              id="findings"
              name="findings"
              value={formData.findings}
              onChange={handleChange}
              rows={5}
              className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
            />
          </div>

          <div>
            <label htmlFor="implications" className="block text-sm font-medium mb-2">
              Practical Implications (HTML)
            </label>
            <textarea
              id="implications"
              name="implications"
              value={formData.implications}
              onChange={handleChange}
              rows={5}
              className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
            />
          </div>

          {/* Tags & Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="tags" className="block text-sm font-medium mb-2">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
                placeholder="tag1, tag2, tag3"
              />
            </div>
            <div>
              <label htmlFor="reading_time" className="block text-sm font-medium mb-2">
                Reading Time (minutes)
              </label>
              <input
                type="number"
                id="reading_time"
                name="reading_time"
                value={formData.reading_time}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
              />
            </div>
            <div>
              <label htmlFor="validation_status" className="block text-sm font-medium mb-2">
                Validation Status
              </label>
              <select
                id="validation_status"
                name="validation_status"
                value={formData.validation_status}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface-dark-2 border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-dark"
              >
                <option value="preliminary">Preliminary</option>
                <option value="validated">Validated</option>
                <option value="peer-reviewed">Peer Reviewed</option>
              </select>
            </div>
          </div>

          {/* Published Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="published"
              name="published"
              checked={formData.published}
              onChange={handleChange}
              className="w-4 h-4"
            />
            <label htmlFor="published" className="text-sm font-medium">
              Publish immediately
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-accent-dark text-white rounded-lg hover:bg-accent-dark/90 disabled:opacity-50 font-medium"
            >
              {saving ? 'Saving...' : (slug ? 'Update Note' : 'Create Note')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/notes')}
              className="px-6 py-3 bg-surface-dark-2 border border-border-dark rounded-lg hover:bg-surface-dark-3"
            >
              Cancel
            </button>
          </div>
        </form>

        <Footer />
      </div>
    </div>
  )
}

